import { supabase } from "@/integrations/supabase/client";
import { connectorActionError } from "@/lib/connectorActionError";

import {
  CONNECTED_FILE_READ_CONTRACT,
  parseConnectedFileReadRequest,
  connectedFileUuid,
  type ConnectedFileProvider,
  type ConnectedFileCoverage,
  type ConnectedFileItem,
  type ConnectedFileReadReply,
} from "../../supabase/functions/_shared/connectedFileReadDomain";

export const FILE_READ_CONTRACT = CONNECTED_FILE_READ_CONTRACT;
export type FileReadProvider = ConnectedFileProvider;
export type FileReadCoverage = ConnectedFileCoverage;
export type FileReadItem = ConnectedFileItem;
export interface FileReadIdentity { auth_session_id: string; connection_id: string }
export type FileSearchReply = ConnectedFileReadReply & {
  action: "search"; query: string; items: FileReadItem[]; next_cursor: string | null;
};
export type FileGetReply = ConnectedFileReadReply & { action: "get"; item: FileReadItem };
type FileReadRequest =
  | { provider: FileReadProvider; action: "search"; query: string; limit: number; cursor?: string }
  | { provider: FileReadProvider; action: "get"; item_id: string };
const object = (value: unknown): value is Record<string, unknown> => !!value && typeof value === "object" && !Array.isArray(value);
const nullableString = (value: unknown) => value === null || typeof value === "string";
const natural = (value: unknown) => Number.isSafeInteger(value) && (value as number) >= 0;

const reasons = new Set(["more_results", "incomplete_search", "char_limit", "byte_limit", "depth_limit", "page_limit", "block_limit", "unsupported_blocks", "content_unavailable", "first_sheet_only", "folder"]);
const truncationReasons = new Set(["more_results", "char_limit", "byte_limit", "depth_limit", "page_limit", "block_limit"]);
const boundedString = (value: unknown, max: number) => typeof value === "string" && value.length <= max;
const nullableBoundedString = (value: unknown, max: number) => value === null || boundedString(value, max);
function safeSourceUrl(value: unknown) {
  if (value === null) return true;
  if (!boundedString(value, 2000)) return false;
  try { const url = new URL(value as string); return url.protocol === "https:" && !url.username && !url.password; }
  catch { return false; }
}
function validItem(value: unknown, provider: FileReadProvider, coverage?: FileReadCoverage): value is FileReadItem {
  if (!object(value) || !boundedString(value.id, 500) || !value.id || !boundedString(value.name, 500) ||
    !["file", "folder", "page"].includes(String(value.kind)) || !nullableBoundedString(value.mime_type, 200) ||
    !safeSourceUrl(value.url) || !nullableBoundedString(value.modified_at, 80) || !nullableBoundedString(value.parent_id, 500)) return false;
  try { parseConnectedFileReadRequest({ read_contract: FILE_READ_CONTRACT, expected_owner_id: "00000000-0000-4000-8000-000000000000", provider, action: "get", item_id: value.id }); }
  catch { return false; }
  if (!coverage) return value.content === undefined;
  const content = value.content;
  if (!object(content) || !nullableBoundedString(content.text, 20000) || !nullableString(content.reason) ||
    content.reason !== null && !reasons.has(content.reason as string) ||
    !["plain_text", "csv_first_sheet", "notion_blocks", "unavailable"].includes(String(content.format)) ||
    typeof content.truncated !== "boolean" || content.truncated !== coverage.truncated ||
    (content.format === "unavailable") !== (content.text === null) ||
    (content.reason === null ? coverage.reasons.length !== 0 : !coverage.reasons.includes(content.reason as FileReadCoverage["reasons"][number]))) return false;
  if (content.format === "unavailable" && (coverage.complete || content.truncated || !["content_unavailable", "folder"].includes(String(content.reason)))) return false;
  if (content.format === "csv_first_sheet" && (provider !== "google_drive" || coverage.complete || !coverage.reasons.includes("first_sheet_only"))) return false;
  if (content.format !== "notion_blocks") return content.blocks === undefined && coverage.blocks_read === 0;
  return provider === "notion" && Array.isArray(content.blocks) && content.blocks.length <= 200 && content.blocks.length === coverage.blocks_read &&
    content.blocks.every(block => object(block) && boundedString(block.id, 500) && boundedString(block.type, 200) && natural(block.depth) && (block.depth as number) <= 3 &&
      boundedString(block.text, 20000) && typeof block.supported === "boolean" && typeof block.has_children === "boolean");
}
function validCoverage(value: unknown): value is FileReadCoverage {
  return object(value) && typeof value.complete === "boolean" && typeof value.truncated === "boolean" &&
    Array.isArray(value.reasons) && value.reasons.length <= reasons.size && value.reasons.every(reason => typeof reason === "string" && reasons.has(reason)) &&
    new Set(value.reasons).size === value.reasons.length && value.complete === (value.reasons.length === 0) &&
    value.truncated === value.reasons.some(reason => truncationReasons.has(reason)) &&
    natural(value.pages_read) && (value.pages_read as number) <= 13 && natural(value.blocks_read) && (value.blocks_read as number) <= 200 &&
    value.char_limit === 20000 && (value.depth_limit === null || value.depth_limit === 3) &&
    [null, "name_and_full_text", "titles", "filenames"].includes(value.search_scope as null | string);
}

export function connectedFileReadSessionId(token: string): string | null {
  try {
    const part = token.split(".")[1];
    const payload = JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/")));
    return connectedFileUuid(payload.session_id) ? payload.session_id : null;
  } catch { return null; }
}
async function currentSession(owner: string) {
  const { data: { session }, error } = await supabase.auth.getSession();
  const sid = session?.access_token ? connectedFileReadSessionId(session.access_token) : null;
  if (error || session?.user.id !== owner || !sid) throw new Error("invalid_session");
  return { token: session.access_token, sid };
}

/** Explicit reads only. A reply cannot survive a changed owner, login, connection or request. */
export async function requestConnectedFileRead(
  owner: string,
  request: FileReadRequest,
  options: {
    signal: AbortSignal;
    identity?: FileReadIdentity;
    onInvokeError?: (error: unknown) => Promise<never>;
  },
): Promise<FileSearchReply | FileGetReply> {
  if (!owner || owner === "signed-out" || owner === "unresolved") throw new Error("invalid_session");
  const body = parseConnectedFileReadRequest({ ...request, read_contract: FILE_READ_CONTRACT, expected_owner_id: owner });
  const initial = await currentSession(owner);
  if (options.identity && options.identity.auth_session_id !== initial.sid) throw new Error("invalid_session");
  let changed = false;
  const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
    if (session?.user.id !== owner || connectedFileReadSessionId(session.access_token) !== initial.sid) changed = true;
  });
  const assertCurrent = async () => {
    const latest = await currentSession(owner);
    if (changed || latest.sid !== initial.sid) throw new Error("invalid_session");
    if (options.signal.aborted) throw new Error("file_read_cancelled");
    return latest.token;
  };
  try {
    const token = await assertCurrent();
    const { data, error } = await supabase.functions.invoke(request.provider === "dropbox" ? "expansion-executor" : "files-executor", {
      body,
      headers: { Authorization: "Bearer " + token },
      signal: options.signal,
    });
    await assertCurrent();
    if (error) {
      if (options.onInvokeError) return await options.onInvokeError(error);
      throw await connectorActionError(error);
    }
    if (data?.error) throw new Error(String(data.error));
    if (!object(data) || data.ok !== true || data.read_contract !== FILE_READ_CONTRACT ||
      data.provider !== request.provider || data.action !== request.action || data.owner_id !== owner ||
      data.auth_session_id !== initial.sid || !connectedFileUuid(data.connection_id) ||
      !validCoverage(data.coverage)) throw new Error("invalid_file_read");
    if (options.identity && options.identity.connection_id !== data.connection_id) throw new Error("connection_changed");
    if (request.action === "search") {
      if (request.provider !== "google_drive" && !data.coverage.reasons.includes("incomplete_search") || data.coverage.search_scope !== ({ google_drive: "name_and_full_text", onedrive: "name_and_full_text", notion: "titles", dropbox: "filenames" } as const)[request.provider] || data.query !== request.query || !Array.isArray(data.items) || data.items.length > request.limit ||
        !data.items.every(item => validItem(item, request.provider)) || new Set(data.items.map(item => item.id)).size !== data.items.length ||
        !(data.next_cursor === null || typeof data.next_cursor === "string" && data.next_cursor.length > 0 && data.next_cursor.length <= 16000) ||
        data.next_cursor !== null && data.coverage.complete ||
        request.cursor !== undefined && data.next_cursor === request.cursor) throw new Error("invalid_file_read");
      return data as unknown as FileSearchReply;
    }
    if (data.coverage.search_scope !== null || !validItem(data.item, request.provider, data.coverage) || (request.provider === "notion" ? data.item.id.replace(/-/g, "").toLowerCase() !== request.item_id.replace(/-/g, "").toLowerCase() : data.item.id !== request.item_id) ||
      data.item.content?.truncated && data.coverage.complete) throw new Error("invalid_file_read");
    return data as unknown as FileGetReply;
  } finally { subscription.unsubscribe(); }
}

/** Provider text is displayed as content; unknown limits never become a claim of completeness. */
export function fileReadCoverageKeys(coverage: FileReadCoverage, item?: FileReadItem): string[] {
  const keys = new Set<string>();
  if (item?.content?.format === "unavailable") keys.add("mirror.files.read.unavailable");
  else if (!coverage.complete || coverage.truncated || item?.content?.truncated) keys.add("mirror.files.read.partial");
  else keys.add(item ? "mirror.files.read.complete" : "mirror.files.read.searchComplete");
  if (item?.content?.format === "csv_first_sheet") keys.add("mirror.files.read.firstSheet");
  if (item?.content?.format === "notion_blocks") keys.add("mirror.files.read.notionChildren");
  if (coverage.truncated || item?.content?.truncated) keys.add("mirror.files.read.limitReached");
  if (coverage.search_scope === "titles") keys.add("mirror.files.read.titlesOnly");
  if (coverage.search_scope === "filenames") keys.add("mirror.files.read.namesOnly");
  if (item?.kind === "folder") keys.add("mirror.files.read.folderMetadata");
  if (coverage.reasons.includes("unsupported_blocks")) keys.add("mirror.files.read.unsupportedBlocks");
  if (coverage.reasons.includes("depth_limit")) keys.add("mirror.files.read.depthLimit");
  if (coverage.reasons.includes("incomplete_search")) keys.add("mirror.files.read.incompleteSearch");
  return [...keys];
}
